import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAppointmentTypes, useBookingById, useProviderDetails } from 'src/modules/booking/hooks'
import { useNoReferrerPolicy } from 'src/modules/booking/hooks/use-no-referrer-policy'
import { useRescheduleAllowed } from 'src/modules/reschedule/hooks'
import {
  buildCalendarEventData,
  buildConfirmedLocationRow,
  computeEndTimeDisplay,
  findAppointmentType,
  formatCancelBy,
  getDisplayDataFromBooking,
  getDisplayDataFromBookingData,
  getEmptyDisplayData,
  getTimeRangeLabel,
  hasBookingData,
  isFreshBooking,
} from './booking-confirmed-step.domain'
import type { AppointmentTypeForConfirmed, BookingConfirmedStepProps } from './booking-confirmed-step.interface'
import { useCustomerAddressHandOff } from './use-customer-address-hand-off'

export function useBookingConfirmedStep({
  providerId: propProviderId,
  bookingId: propBookingId,
  bookingData,
}: BookingConfirmedStepProps) {
  const params = useParams()
  useNoReferrerPolicy()
  const customerAddress = useCustomerAddressHandOff()
  const bookingId = propBookingId ?? params.bookingId ?? ''
  const providerId = propProviderId ?? params.providerId ?? ''

  const hasPropsData = hasBookingData(bookingData)

  const { data: booking, isLoading: isLoadingBooking } = useBookingById(bookingId, {
    enabled: !!bookingId && !hasPropsData,
  })

  const { data: providerDetails } = useProviderDetails(providerId, { enabled: !!providerId })
  const { data: appointmentTypes = [] } = useAppointmentTypes(providerId, { enabled: !!providerId })

  const displayData = useMemo(() => {
    if (hasPropsData && bookingData) {
      return getDisplayDataFromBookingData(bookingData)
    }
    if (!booking) return getEmptyDisplayData()
    return getDisplayDataFromBooking(booking)
  }, [hasPropsData, bookingData, booking])

  const appointmentType = useMemo(
    () => findAppointmentType(appointmentTypes as AppointmentTypeForConfirmed[], displayData.appointmentType),
    [appointmentTypes, displayData.appointmentType],
  )

  const providerName = providerDetails?.full_name ?? 'Provider'
  const canReschedule = useRescheduleAllowed(bookingId)

  const calendarEventData = useMemo(
    () =>
      buildCalendarEventData({
        bookingId,
        booking: booking ?? null,
        bookingData: bookingData ?? null,
        displayData,
        appointmentType,
        providerName,
        customerAddress,
        canReschedule,
      }),
    [bookingId, booking, bookingData, displayData, appointmentType, providerName, customerAddress, canReschedule],
  )

  const locationRow = useMemo(() => buildConfirmedLocationRow(booking, customerAddress), [booking, customerAddress])

  const timeRangeLabel = useMemo(() => {
    if (!displayData.selectedTime) return ''
    const durationMinutes = appointmentType?.duration_minutes ?? 30
    const endTime = displayData.endTime ?? computeEndTimeDisplay(displayData.selectedTime, durationMinutes)
    return getTimeRangeLabel(displayData.selectedTime, endTime, displayData.timezone)
  }, [displayData.selectedTime, displayData.endTime, displayData.timezone, appointmentType])

  const handleCtaClick = useCallback(() => {
    window.open('https://www.smsreminder.co/', '_blank', 'noopener,noreferrer')
  }, [])

  const isLoading = isLoadingBooking && !hasPropsData
  const showNotFound = !booking && !hasPropsData && !!bookingId

  const showEmailOnItsWay = hasPropsData || isFreshBooking(booking)

  // The props path carries no server policy, so it shows no section.
  const cancellationPolicy = hasPropsData ? null : (booking?.cancellation_policy ?? null)
  const cancelByLabel = useMemo(
    () => (cancellationPolicy && !cancellationPolicy.short_notice ? formatCancelBy(cancellationPolicy) : null),
    [cancellationPolicy],
  )

  return {
    isLoading,
    showNotFound,
    displayData,
    providerName,
    logoUrl: providerDetails?.logo_url,
    appointmentType,
    calendarEventData,
    locationRow,
    timeRangeLabel,
    canReschedule,
    showEmailOnItsWay,
    cancellationPolicy,
    cancelByLabel,
    handleCtaClick,
  }
}
